import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  useEffect, useState, useCallback, useMemo,
} from 'react';
import _ from 'lodash';
import { useRouter } from 'next/router';
import { TravelTypes } from '../../../../constants';
import AddUpdateBrochure from '..';
import getBrochureTripId from '../../../../helpers/getBrochureTripId';
import useHandleFlightAddToBrochure from '../../../../hooks/useHandleFlightAddToBrochure';
import arePassengerCountsEqual from '../../../../helpers/checkArePassengerCountsEqual';
import isOutboundFlightLegExistsInBrochure from '../../../../helpers/checkIfFlightItineraryExistsInBrochure';

const FlightAddUpdateToBrochure = ({
  tripId, flightItinerary, className, tripRoomSearchInput,
}) => {
  const router = useRouter();
  const { brochure } = useSelector((state) => state.brochure);
  const [isSelected, setIsSelected] = useState(false);
  const { isViewingBrochure } = useSelector((state) => state.brochure);
  const isBrochurePage = router.pathname.indexOf('brochure') > -1;
  const currentBrochure = useSelector((state) => state.brochure.brochures[brochure && brochure.id]);
  const brochureOnBrochurePage = useSelector((state) => state.brochure.brochures[tripId]);
  const { staterooms: stateroomsObj } = useSelector((store) => store.tripSearch.searchObj);
  // Handle adding/updating trip segments
  const id = useMemo(() => (
    getBrochureTripId(tripId, brochure, flightItinerary,
      tripRoomSearchInput || stateroomsObj, TravelTypes.flight)
  ), [brochure, flightItinerary, tripId, stateroomsObj, tripRoomSearchInput]);

  const findFlightItinerary = (tripSegments, flightKey, staterooms) => _.find(tripSegments,
    (i) => (i.itinerary.flightKey === flightKey
    && i.itinerary?.startDateTime === flightItinerary?.startDateTime
    && i.itinerary?.endDateTime === flightItinerary?.endDateTime
    && arePassengerCountsEqual(staterooms, tripRoomSearchInput || stateroomsObj)));

  const findFlightSegments = (tripSegments) => _.filter(tripSegments,
    (ts) => ts.itinerary.mode === TravelTypes.flight);

  const setSelectedTripSegment = useCallback((tripSegments, staterooms) => {
    if (tripSegments && tripSegments.length > 0
      && findFlightItinerary(tripSegments, flightItinerary.flightKey, staterooms)) {
      setIsSelected(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightItinerary.flightKey]);

  useEffect(() => {
    setIsSelected(false);

    const findAndSetTripSegments = (brochureData, tripIdentifier) => {
      if (!brochureData || !brochureData.trips || brochureData.trips.length === 0) {
        return;
      }

      const isMatchingFlightSegment = (segment, trip) => {
        const itinerary = segment?.itinerary;

        return itinerary?.mode === TravelTypes.flight
          && itinerary?.flightKey === flightItinerary?.flightKey
          && itinerary?.startDateTime === flightItinerary?.startDateTime
          && itinerary?.endDateTime === flightItinerary?.endDateTime
          && arePassengerCountsEqual(tripRoomSearchInput, trip?.roomSearchInput
            ?? trip?.staterooms);
      };

      const isMatchingTrip = (trip) => trip.id === tripIdentifier
        && trip?.tripSegments?.some((segment) => isMatchingFlightSegment(segment, trip));

      const foundTrip = brochureData.trips.find(isMatchingTrip);

      if (foundTrip && foundTrip.tripSegments) {
        setSelectedTripSegment(foundTrip.tripSegments, foundTrip?.staterooms
          ?? foundTrip?.roomSearchInput);
      }
    };

    if (isBrochurePage && brochureOnBrochurePage && tripId) {
      findAndSetTripSegments(brochureOnBrochurePage, tripId);
      return;
    }

    if (isViewingBrochure && currentBrochure && brochure && brochure.id) {
      findAndSetTripSegments(currentBrochure, brochure.id);
      return;
    }

    if (brochure && brochure.trips && brochure.trips[id]) {
      const tripSegments = findFlightSegments(brochure.trips[id].tripSegments);
      if (tripSegments && tripSegments.length > 0) {
        setSelectedTripSegment(tripSegments, brochure?.trips?.[id]?.staterooms
          ?? brochure?.trips?.[id]?.roomSearchInput);
      }
    }
  }, [
    brochure,
    brochureOnBrochurePage,
    currentBrochure,
    flightItinerary,
    isBrochurePage,
    isSelected,
    isViewingBrochure,
    setSelectedTripSegment,
    tripId,
    id,
    stateroomsObj,
    tripRoomSearchInput,
  ]);
  const isItineraryInBrochure = isOutboundFlightLegExistsInBrochure(brochure,
    flightItinerary.flightKey);

  const handleAddToBrochureClick = useHandleFlightAddToBrochure({
    flightItinerary,
    tripId,
    brochure,
    isViewingBrochure,
    isBrochurePage,
    currentBrochure,
    brochureOnBrochurePage,
    tripRoomSearchInput,
  });
  return (
    <AddUpdateBrochure
      handleAddToBrochureClick={handleAddToBrochureClick}
      className={className}
      isSelected={isSelected || !!isItineraryInBrochure}
    />
  );
};

FlightAddUpdateToBrochure.propTypes = {
  className: PropTypes.string,
  tripId: PropTypes.string.isRequired,
  flightItinerary: PropTypes.shape().isRequired,
  tripRoomSearchInput: PropTypes.arrayOf(PropTypes.shape()),
};

FlightAddUpdateToBrochure.defaultProps = {
  className: '',
  tripRoomSearchInput: undefined,
};
export default FlightAddUpdateToBrochure;
